import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff, Heart, Loader2, RefreshCw, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useContentStore } from '@/store/contentStore';
import { LANGUAGE_NAMES, SOURCE_LANGUAGE_LABELS, type CardStatus, type ContentCard, type TranslationStatus, type UpdateContentRequest } from '@/types/content';
import { PageHeader } from '@/components/PageHeader';
import { ContentForm, formValuesFromCard, type ContentFormValues } from '@/components/ContentForm';
import { StatusBadge, TranslationStatusBadge } from '@/components/badges';
import { GenerationStatusBadge } from '@/components/GenerationStatusCell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Alert, Skeleton, Switch } from '@/components/ui/misc';

type PendingAction = 'delete' | 'publish' | 'unpublish' | null;

export function ContentEdit() {
  const { cardId } = useParams<{ cardId: string }>();
  const navigate = useNavigate();
  const { currentCard, fetchCard, updateCard, deleteCard, updateCardStatus, retryTranslations, stopPollingTranslations, stopPollingGeneration, loading, error, clearError } = useContentStore();

  const [values, setValues] = useState<ContentFormValues | null>(null);
  const [regenerateTranslations, setRegenerateTranslations] = useState(false);
  // Legacy imageUrl written by the old "Card Image" box; the app never shows it (VAP-824)
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState<PendingAction>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // fetchCard starts the translation/generation pollers for this card; stop them on leave
  useEffect(() => {
    if (cardId) fetchCard(cardId);
    return () => {
      stopPollingTranslations();
      stopPollingGeneration();
    };
  }, [cardId, fetchCard, stopPollingTranslations, stopPollingGeneration]);

  // Tell the admin when the background translation finishes. The store poller updates
  // currentCard every 5 s while the card is TRANSLATING; this fires once on the change.
  // The ref is reset per card id, so opening another card never fires a toast about it.
  const lastTranslationStatus = useRef<{ cardId: string | undefined; status: TranslationStatus | undefined }>({ cardId: undefined, status: undefined });
  useEffect(() => {
    const status = currentCard && currentCard.cardId === cardId ? currentCard.translationStatus : undefined;
    const previous = lastTranslationStatus.current.cardId === cardId ? lastTranslationStatus.current.status : undefined;
    lastTranslationStatus.current = { cardId, status };
    if (previous !== 'TRANSLATING' || status === 'TRANSLATING' || !status) return;
    if (status === 'TRANSLATED') toast.success('All translations are ready');
    else if (status === 'PARTIAL') toast.warning('Some translations failed. Use Retry to run them again.');
    else if (status === 'FAILED') toast.error('Translation failed. Use Retry to run it again.');
  }, [currentCard, cardId]);

  // Fill the form once per card id. The store pollers replace currentCard every 5 s
  // while a card translates or generates; that must not throw away what the admin typed.
  // A save refills on purpose (see handleSubmit).
  const filledForCardId = useRef<string | null>(null);
  useEffect(() => {
    if (currentCard && currentCard.cardId === cardId && filledForCardId.current !== cardId) {
      filledForCardId.current = cardId ?? null;
      setValues(formValuesFromCard(currentCard));
      setImageUrl(currentCard.imageUrl);
    }
  }, [currentCard, cardId]);

  const baseline = useMemo(() => (currentCard ? formValuesFromCard(currentCard) : null), [currentCard]);
  const hasChanges = Boolean(
    values && baseline && (JSON.stringify(values) !== JSON.stringify(baseline) || imageUrl !== currentCard?.imageUrl)
  );
  const hrChanged = Boolean(values?.hr && baseline?.hr && JSON.stringify(values.hr) !== JSON.stringify(baseline.hr));

  const handleSubmit = async () => {
    if (!cardId || !values || !currentCard) return;

    const data: UpdateContentRequest = {
      cardType: values.cardType,
      title: values.title.trim(),
      summary: values.summary.trim() || values.title.trim(),
      body: values.body,
      publishDate: values.publishDate,
      // null clears the date (JSON drops undefined, so the Lambda would never see a clear)
      expirationDate: values.expirationDate || null,
      isPushFlagged: values.isPushFlagged,
      regenerateTranslations,
      sourceLanguage: values.sourceLanguage,
      // Always send the list: an empty array clears media (undefined would keep the old list)
      mediaUrls: values.mediaUrls,
      externalLinks: values.externalLinks.length > 0 ? values.externalLinks : undefined,
      // null removes the legacy image, undefined leaves it unchanged
      imageUrl: imageUrl !== currentCard.imageUrl ? (imageUrl === undefined ? null : imageUrl) : undefined,
    };
    if (hrChanged && values.hr) {
      data.croatianContent = {
        title: values.hr.title,
        summary: values.hr.summary || values.hr.title,
        body: values.hr.body,
      };
    }

    const card = await updateCard(cardId, data);
    if (card) {
      // Refill from the saved card so the baseline and the form agree
      setValues(formValuesFromCard(card));
      setImageUrl(card.imageUrl);
      setRegenerateTranslations(false);
      toast.success(regenerateTranslations ? 'Saved. Translations are being regenerated.' : 'Changes saved');
    }
  };

  const runPending = async () => {
    if (!cardId || !pending) return;
    setActionBusy(true);
    let ok = false;
    if (pending === 'delete') {
      ok = await deleteCard(cardId);
      if (ok) {
        toast.success('Card deleted');
        navigate('/content');
      }
    } else {
      const status: CardStatus = pending === 'publish' ? 'PUBLISHED' : 'DRAFT';
      ok = (await updateCardStatus(cardId, status)) !== null;
      if (ok) toast.success(pending === 'publish' ? 'Card published' : 'Card unpublished');
    }
    setActionBusy(false);
    if (ok) setPending(null);
  };

  if (loading && !currentCard) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-72" />
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
          <div className="space-y-6 xl:col-span-3">
            <Skeleton className="h-28" />
            <Skeleton className="h-72" />
            <Skeleton className="h-40" />
          </div>
          <Skeleton className="h-[520px] xl:col-span-2" />
        </div>
      </div>
    );
  }

  if (!currentCard || !values) {
    return (
      <Card className="flex flex-col items-center justify-center px-6 py-16 text-center">
        <AlertCircle className="mb-4 h-10 w-10 text-muted-foreground" />
        <p className="font-medium">Content card not found</p>
        <Button variant="link" onClick={() => navigate('/content')}>Back to content</Button>
      </Card>
    );
  }

  const expirationError =
    values.expirationDate && values.expirationDate < values.publishDate
      ? 'The expiration date must not be before the publish date.'
      : undefined;

  return (
    <div>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            Edit content
            <StatusBadge status={currentCard.status} />
            {hasChanges && <span className="text-sm font-normal text-accent">Unsaved changes</span>}
          </span>
        }
        description={<span className="font-mono text-xs">{currentCard.cardId}</span>}
        backTo="/content"
        actions={
          <>
            {currentCard.status === 'DRAFT' && (
              <Button variant="success" onClick={() => setPending('publish')}>
                <Send />
                Publish
              </Button>
            )}
            {currentCard.status === 'PUBLISHED' && (
              <Button variant="warning" onClick={() => setPending('unpublish')}>
                <EyeOff />
                Unpublish
              </Button>
            )}
            <Button variant="outline" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setPending('delete')}>
              <Trash2 />
              Delete
            </Button>
          </>
        }
      />

      {error && (
        <Alert variant="error" title="Could not save" onDismiss={clearError} className="mb-6">
          {error}
        </Alert>
      )}

      {currentCard.status === 'DRAFT' && (
        <Alert variant="warning" title="This card is not visible to users" className="mb-6">
          Publish it to show it in the app.
        </Alert>
      )}

      {/* Status strip */}
      <Card className="mb-6">
        <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-3 p-4 text-sm">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Eye className="h-4 w-4" aria-hidden />
            <span className="font-medium text-foreground tabular-nums">{currentCard.metrics.fetchCount}</span> fetches
          </span>
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Heart className="h-4 w-4" aria-hidden />
            <span className="font-medium text-foreground tabular-nums">{currentCard.metrics.likeCount}</span> likes
          </span>
          <span className="flex items-center gap-2 text-muted-foreground">
            Generation <GenerationStatusBadge status={currentCard.generationStatus ?? 'NONE'} />
          </span>
          <span className="flex items-center gap-2 text-muted-foreground">
            Translations <TranslationStatusBadge status={currentCard.translationStatus ?? 'NONE'} />
          </span>
          <span className="ml-auto text-muted-foreground">
            Created {new Date(currentCard.createdAt).toLocaleDateString()}
          </span>
        </CardContent>
        <TranslationStatusLine
          card={currentCard}
          retrying={retrying}
          onRetry={async () => {
            if (!cardId) return;
            setRetrying(true);
            const ok = await retryTranslations(cardId);
            setRetrying(false);
            if (ok) toast.success('Translation started again');
          }}
        />
      </Card>

      <ContentForm
        values={values}
        onChange={setValues}
        onSubmit={handleSubmit}
        submitLabel={regenerateTranslations ? 'Save and regenerate translations' : 'Save changes'}
        submitting={loading}
        cardId={cardId}
        previewBase={currentCard}
        lockSourceLanguage
        errors={{ expirationDate: expirationError }}
        optionsSlot={
          <Card>
            <CardHeader>
              <CardTitle>Translations</CardTitle>
              <CardDescription>Regenerate the 9 other languages from the {SOURCE_LANGUAGE_LABELS[values.sourceLanguage]} text on save.</CardDescription>
            </CardHeader>
            <CardContent>
              <label className="flex items-center justify-between gap-4 rounded-md border border-border p-3">
                <span className="flex items-center gap-3">
                  <RefreshCw className="h-4 w-4 text-muted-foreground" aria-hidden />
                  <span>
                    <span className="block text-sm font-medium">Regenerate translations</span>
                    <span className="block text-xs text-muted-foreground">
                      {values.sourceLanguage === 'hr' ? 'Replaces every translation, including the English one.' : 'Replaces every translation, including a Croatian edit.'}
                    </span>
                  </span>
                </span>
                <Switch checked={regenerateTranslations} onCheckedChange={setRegenerateTranslations} aria-label="Regenerate translations" />
              </label>
            </CardContent>
          </Card>
        }
        mediaSlot={
          imageUrl ? (
            <Alert variant="warning" title="Legacy card image">
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <img src={imageUrl} alt="" className="h-12 w-12 shrink-0 rounded object-cover bg-muted" />
                <span className="flex-1 text-xs">This image is not shown in the app. Add it to Media, or remove it.</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={values.mediaUrls.some(m => m.url === imageUrl)}
                  onClick={() => setValues({ ...values, mediaUrls: [...values.mediaUrls, { type: 'image', url: imageUrl }] })}
                >
                  Add to Media
                </Button>
                <Button type="button" size="sm" variant="ghost" className="text-destructive" onClick={() => setImageUrl(undefined)}>
                  Remove
                </Button>
              </div>
            </Alert>
          ) : null
        }
      />

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => { if (!open) setPending(null); }}
        title={pending === 'delete' ? 'Delete this card?' : pending === 'publish' ? 'Publish this card?' : 'Unpublish this card?'}
        description={
          pending === 'delete'
            ? 'This cannot be undone. The card and its uploaded images are removed.'
            : pending === 'publish'
              ? 'The card becomes visible to all app users.'
              : 'The card is hidden from all app users and goes back to Draft.'
        }
        confirmLabel={pending === 'delete' ? 'Delete' : pending === 'publish' ? 'Publish' : 'Unpublish'}
        confirmVariant={pending === 'delete' ? 'destructive' : pending === 'publish' ? 'success' : 'warning'}
        loading={actionBusy}
        onConfirm={runPending}
      />
    </div>
  );
}

interface TranslationStatusLineProps {
  card: ContentCard;
  retrying: boolean;
  onRetry: () => void;
}

/**
 * Plain-language line under the status strip that tells the admin what the translation
 * badge means and what to do: wait (the page updates by itself), nothing, or Retry.
 */
function TranslationStatusLine({ card, retrying, onRetry }: TranslationStatusLineProps) {
  const { translationStatus, translationProgress } = card;
  if (!translationStatus || translationStatus === 'NONE') return null;

  const langs = translationProgress
    ? (Object.keys(translationProgress) as Array<keyof typeof LANGUAGE_NAMES>).filter(l => l in LANGUAGE_NAMES)
    : [];
  const completed = langs.filter(l => translationProgress?.[l] === 'completed');
  const failed = langs.filter(l => translationProgress?.[l] === 'failed');
  const total = langs.length;

  let text: string;
  let tone = 'text-muted-foreground';
  switch (translationStatus) {
    case 'TRANSLATING':
      text = total > 0
        ? `Translating: ${completed.length} of ${total} languages ready. This page updates by itself, you can stay or leave.`
        : 'Translating. This page updates by itself, you can stay or leave.';
      break;
    case 'TRANSLATED':
      text = total > 0 ? `All ${total} translations are ready.` : 'All translations are ready.';
      break;
    case 'PARTIAL':
      text = total > 0
        ? `${failed.length} of ${total} translations failed${failed.length > 0 ? ` (${failed.map(l => LANGUAGE_NAMES[l]).join(', ')})` : ''}. Retry runs only the failed ones.`
        : 'Some translations failed. Retry runs only the failed ones.';
      tone = 'text-warning';
      break;
    case 'FAILED':
      text = 'Translation failed for every language. Retry runs it again.';
      tone = 'text-destructive';
      break;
    default:
      return null;
  }

  const canRetry = translationStatus === 'PARTIAL' || translationStatus === 'FAILED';

  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border px-4 py-2.5 text-sm ${tone}`} role="status">
      {translationStatus === 'TRANSLATING' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      <span>{text}</span>
      {canRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} disabled={retrying} className="h-7">
          {retrying ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          Retry
        </Button>
      )}
    </div>
  );
}
